import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {
    EntityTag,
    IApiUser,
    IAuth,
    ICreateApiUser,
    ICreateGallery,
    ICreateOrganisationWithApiUser,
    IEvent,
    IEventAnalytics,
    IGallery,
    IGalleryPhoto, IMatchingPhoto,
    IOrganisation,
    IUpdateApiUser,
} from "./types";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const api = createApi({
    tagTypes: [
        EntityTag.Organisation,
        EntityTag.Event,
        EntityTag.Gallery,
        EntityTag.Photo,
        EntityTag.SystemSettings,
    ],
    baseQuery: fetchBaseQuery({ baseUrl: apiBaseUrl }),
    endpoints: (build) => ({
        login: build.mutation<IAuth, { email: string, password: number }>({
            query: (params) => ({
                url: `/auth/login`,
                method: 'POST',
                body: { username: params.email, password: params.password },
            }),
        }),
        resetPassword: build.mutation<void, { email: string }>({
            query: (params) => ({
                url: `/auth/reset-password`,
                method: 'PUT',
                body: { username: params.email },
            }),
        }),
        updatePassword: build.mutation<IApiUser, { token: string, password: string }>({
            query: (params) => ({
                url: `/auth/update-password`,
                method: 'PUT',
                body: { token: params.token, password: params.password },
            }),
        }),
        getProfile: build.mutation<IApiUser, { jwtToken: string }>({
            query: (params) => ({
                url: `/auth/profile`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
        }),
        getUsers: build.query<{data: IApiUser[]}, { jwtToken: string }>({
            query: (params) => ({
                url: `/users/api-users`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
        }),
        updateUser: build.mutation<IApiUser, {jwtToken: string, payload: IUpdateApiUser}>({
            query: (params) => ({
                url: `/users/api-user`,
                method: 'PUT',
                body: params.payload,
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
        }),
        createUser: build.mutation<IApiUser, {jwtToken: string, payload: ICreateApiUser}>({
            query: (params) => ({
                url: `/users/api-user`,
                method: 'POST',
                body: params.payload,
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
        }),
        createOrganisationWithUser: build.mutation<IApiUser, {payload: ICreateOrganisationWithApiUser}>({
            query: (params) => ({
                url: `/register`,
                method: 'POST',
                body: params.payload,
            }),
        }),
        getOrganisation: build.query<{data: IOrganisation}, { jwtToken: string }>({
            query: (params) => ({
                url: `/organisations`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
        }),
        getAdminOrganisations: build.query<{data: IOrganisation[]}, { jwtToken: string }>({
            query: (params) => ({
                url: `/admin/organisations`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            providesTags: (result) => [
                ...(result?.data || []).map(organisation => ({
                    type: EntityTag.Organisation,
                    id: organisation.id,
                })),
                {
                    type: EntityTag.Organisation,
                    id: 'LIST',
                },
            ],
        }),
        getEventByHash: build.query<IEvent, { hash: string }>({
            query: (params) => ({
                url: `/events/hash/${params.hash}`,
                method: 'GET',
            }),
            providesTags: (result) => result ? [{ type: EntityTag.Event, id: result.id }] : [],
        }),
        getOrganisationEvents: build.query<IEvent[], { jwtToken: string, organisationId: number }>({
            query: (params) => ({
                url: `/events`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            providesTags: (result) => [
                ...(result || []).map(event => ({
                    type: EntityTag.Event,
                    id: event.id,
                })),
                {
                    type: EntityTag.Event,
                    id: 'LIST',
                },
            ],
        }),
        createEvent: build.mutation<IEvent, {jwtToken: string, payload: Omit<IEvent, 'id' | 'hash' | 'status'>}>({
            query: (params) => ({
                url: `/events`,
                method: 'POST',
                body: params.payload,
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            invalidatesTags: [{ type: EntityTag.Event, id: 'LIST' }],
        }),
        
        // Get event by ID
        getEventById: build.query<IEvent, { jwtToken: string, eventId: number }>({
            query: (params) => ({
                url: `/events/${params.eventId}`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            providesTags: (result) => result ? [{ type: EntityTag.Event, id: result.id }] : [],
        }),
        
        // Get event analytics
        getEventAnalytics: build.query<IEventAnalytics, { jwtToken: string, eventId: number }>({
            query: (params) => ({
                url: `/events/${params.eventId}/analytics`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
        }),
        
        // Get gallery by ID
        getGalleryById: build.query<IGallery, { jwtToken: string, galleryId: number }>({
            query: (params) => ({
                url: `/galleries/${params.galleryId}`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            providesTags: (result) => result ? [{ type: EntityTag.Gallery, id: result.id }] : [],
        }),
        
        // Get gallery photos
        getGalleryPhotos: build.query<IGalleryPhoto[], { jwtToken: string, galleryId: number }>({
            query: (params) => ({
                url: `/galleries/items/${params.galleryId}`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            providesTags: (result) => [
                ...(result || []).map(photo => ({
                    type: EntityTag.Photo,
                    id: photo.id,
                })),
                {
                    type: EntityTag.Photo,
                    id: 'LIST',
                },
            ],
        }),
        
        // Upload photos to gallery
        uploadPhotos: build.mutation<IGalleryPhoto[], {
            jwtToken: string,
            formData: FormData,
            onUploadProgress?: (progressEvent: any) => void
        }>({
            query: (params) => ({
                url: `/galleries/upload`,
                method: 'POST',
                body: params.formData,
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                    // Do not set Content-Type here as it will be automatically set with boundary for multipart/form-data
                },
                responseHandler: "json",
                // Add upload progress tracking
                customRequestOptions: {
                    onUploadProgress: params.onUploadProgress,
                },
            }),
            invalidatesTags: [{ type: EntityTag.Photo, id: 'LIST' }],
        }),
        
        // Delete photo from gallery
        deletePhoto: build.mutation<void, { jwtToken: string, photoId: number }>({
            query: (params) => ({
                url: `/galleries/item/${params.photoId}`,
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: EntityTag.Photo, id: arg.photoId },
                { type: EntityTag.Photo, id: 'LIST' }
            ],
        }),
        
        // Delete all photos from gallery
        deleteAllGalleryPhotos: build.mutation<void, { jwtToken: string, galleryHash: string }>({
            query: (params) => ({
                url: `/galleries/hash/${params.galleryHash}/all-items`,
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            invalidatesTags: [{ type: EntityTag.Photo, id: 'LIST' }],
        }),
        
        // Toggle gallery exclusion from facial recognition
        toggleGalleryRecognition: build.mutation<IGallery, { jwtToken: string, galleryId: number, exclude: boolean }>({
            query: (params) => ({
                url: `/galleries/${params.galleryId}/exclude-from-recognition`,
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                    'Content-Type': 'application/json',
                },
                body: { exclude: params.exclude },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: EntityTag.Gallery, id: arg.galleryId }
            ],
        }),
        
        // Toggle gallery public visibility
        toggleGalleryPublicVisibility: build.mutation<IGallery, { jwtToken: string, galleryId: number, publiclyVisible: boolean }>({
            query: (params) => ({
                url: `/galleries/${params.galleryId}/public-visibility`,
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                    'Content-Type': 'application/json',
                },
                body: { isPubliclyVisible: params.publiclyVisible },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: EntityTag.Gallery, id: arg.galleryId }
            ],
        }),
        
        // Find matching photos based on selfie
        findMatchingPhotos: build.mutation<IMatchingPhoto[], {
            eventId: number,
            selfie: File,
            jwtToken?: string
        }>({
            query: (params) => {
                const formData = new FormData();
                formData.append('eventId', params.eventId.toString());
                formData.append('selfie', params.selfie);
                
                return {
                    url: `/galleries/match`,
                    method: 'POST',
                    body: formData,
                    headers: params.jwtToken ? {
                        Authorization: `Bearer ${params.jwtToken}`,
                        // Do not set Content-Type here as it will be automatically set with boundary for multipart/form-data
                    } : undefined,
                    responseHandler: "json",
                };
            },
        }),
        
        // Get public photos from an event
        getEventPublicPhotos: build.query<IGalleryPhoto[], { eventHash: string }>({
            query: (params) => ({
                url: `/events/hash/${params.eventHash}/public-photos`,
                method: 'GET',
            }),
            providesTags: (result) => [
                ...(result || []).map(photo => ({
                    type: EntityTag.Photo,
                    id: photo.id,
                })),
                {
                    type: EntityTag.Photo, 
                    id: 'PUBLIC_LIST'
                },
            ],
        }),
        
        // Get event galleries
        getEventGalleries: build.query<IGallery[], { jwtToken: string, eventId: number }>({
            query: (params) => ({
                url: `/galleries/event/${params.eventId}`,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            providesTags: (result) => [
                ...(result || []).map(gallery => ({
                    type: EntityTag.Gallery,
                    id: gallery.id,
                })),
                {
                    type: EntityTag.Gallery, 
                    id: 'LIST'
                },
            ],
        }),
        
        // Create gallery
        createGallery: build.mutation<IGallery, {jwtToken: string, payload: ICreateGallery}>({
            query: (params) => ({
                url: `/galleries`,
                method: 'POST',
                body: params.payload,
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            invalidatesTags: [{ type: EntityTag.Gallery, id: 'LIST' }],
        }),
        
        // Delete event by hash
        deleteEvent: build.mutation<void, { jwtToken: string, hash: string }>({
            query: (params) => ({
                url: `/events/hash/${params.hash}`,
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${params.jwtToken}`,
                },
            }),
            invalidatesTags: [{ type: EntityTag.Event, id: 'LIST' }],
        }),
    }),
});

export const {
    useLoginMutation,
    useResetPasswordMutation,
    useUpdatePasswordMutation,
    useGetProfileMutation,
    useGetOrganisationQuery,
    useGetUsersQuery,
    useUpdateUserMutation,
    useCreateUserMutation,
    useCreateOrganisationWithUserMutation,
    useGetAdminOrganisationsQuery,
    useGetEventByHashQuery,
    useGetOrganisationEventsQuery,
    useCreateEventMutation,
    useDeleteEventMutation,
    useGetEventByIdQuery,
    useGetEventAnalyticsQuery,
    useGetEventGalleriesQuery,
    useCreateGalleryMutation,
    useGetGalleryByIdQuery,
    useGetGalleryPhotosQuery,
    useUploadPhotosMutation,
    useDeletePhotoMutation,
    useDeleteAllGalleryPhotosMutation,
    useToggleGalleryRecognitionMutation,
    useToggleGalleryPublicVisibilityMutation,
    useFindMatchingPhotosMutation,
    useGetEventPublicPhotosQuery,
} = api;

export default api;